import Silo, { AddSilo } from './Silo'
import { GOOD, PRODUCTS, fmtLitres, sellingLabel } from './constants'
import { tankFillFraction } from '../../utils/dataHelpers'

function Fact({ children }) {
  return (
    <span className="inline-flex items-baseline gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[12.5px] text-gray-600">
      {children}
    </span>
  )
}

function PumpChip({ pump, onClick }) {
  return (
    <div className="flex flex-col items-center">
      <span className="h-3 w-[1.5px] bg-gray-300" />
      <button
        type="button"
        onClick={onClick}
        aria-label={`${pump.name || `Pump ${pump.pumpNumber}`}, calibration ${Number(pump.defaultExcessPer20L ?? 0).toFixed(2)} litres per 20 litres. Open pump settings.`}
        className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 font-mono text-[11.5px] text-gray-600 transition hover:border-[#e2932b] hover:text-gray-900"
      >
        <span
          className="h-[7px] w-[7px] rotate-45 border-b-[1.5px] border-l-[1.5px] border-current opacity-60"
          aria-hidden="true"
        />
        {pump.name || `Pump ${pump.pumpNumber}`} · {Number(pump.defaultExcessPer20L ?? 0).toFixed(2)}
      </button>
    </div>
  )
}

/**
 * One product line. Tanks sit above a single horizontal rail and pumps hang
 * below it — the rail IS the manifold, and the drawing is the explanation:
 * no pump belongs to a tank, they all draw from whichever valve is open.
 */
export default function ProductBand({ line, levels, busyTankId, onOpenTank, onAddTank, onOpenPump, onAddPump, onToggleValve, onOpenLine }) {
  const { product, tanks, pumps } = line
  const meta = PRODUCTS[product] || { name: product, color: '#64748b' }
  const totalCapacity = tanks.reduce((sum, t) => sum + (Number(t.capacity) || 0), 0)
  const selling = sellingLabel(tanks)

  return (
    <section className="mb-[18px] rounded-[22px] border border-gray-200 bg-white px-5 pb-6 pt-5 shadow-sm">
      <header className="mb-[18px] flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span
            className="rounded-[9px] px-3 py-1.5 font-mono text-[13px] font-bold tracking-[.03em] text-white"
            style={{ background: meta.color }}
          >
            {product}
          </span>
          <h3 className="text-[15px] font-bold text-gray-900">{meta.name}</h3>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <Fact><b className="font-semibold text-gray-900">{tanks.length}</b> tank{tanks.length === 1 ? '' : 's'} · <b className="font-semibold text-gray-900 tabular-nums">{fmtLitres(totalCapacity)} L</b></Fact>
          <Fact><b className="font-semibold text-gray-900">{pumps.length}</b> pump{pumps.length === 1 ? '' : 's'} on the shared line</Fact>
          {selling ? (
            <Fact>
              <span className="h-[7px] w-[7px] rounded-full" style={{ background: GOOD, boxShadow: `0 0 0 3px ${GOOD}22` }} />
              Selling from <b className="font-semibold text-gray-900">{selling}</b>
            </Fact>
          ) : (
            <Fact><span className="text-amber-700">No tank selling</span></Fact>
          )}
          <button
            type="button"
            onClick={onOpenLine}
            className="ml-1 rounded-lg px-2 py-1 text-[12px] font-semibold text-gray-400 transition hover:bg-gray-50 hover:text-gray-700"
          >
            Edit line
          </button>
        </div>
      </header>

      {/* Tanks. Fixed-width silos overflow once a line has many tanks, so this
          row scrolls sideways rather than wrapping into a ragged grid. */}
      <div className="-mx-1 flex items-end gap-[22px] overflow-x-auto px-1 pb-1">
        {tanks.map((tank) => (
          <Silo
            key={tank.id}
            tank={tank}
            product={product}
            fill={tankFillFraction(tank, levels)}
            busy={busyTankId === tank.id}
            onOpen={() => onOpenTank(tank)}
            onToggleValve={() => onToggleValve(tank)}
          />
        ))}
        <AddSilo onClick={onAddTank} />
      </div>

      {/* The manifold. The caption sits over the pump row's right-hand space on
          wide screens; narrow ones have no room for that, so it gets its own line. */}
      <div className="relative mx-1.5 h-0.5 rounded-sm bg-gray-300">
        <span className="absolute right-0 top-[7px] hidden text-[10.5px] font-semibold uppercase tracking-[.05em] text-gray-400 desk:block">
          Shared line — every pump draws from the open tank
        </span>
      </div>
      <p className="mt-2 text-[10px] font-semibold uppercase tracking-[.05em] text-gray-400 desk:hidden">
        Shared line — every pump draws from the open tank
      </p>

      <div className="mt-1 flex flex-wrap items-start gap-3.5 desk:mt-0">
        {pumps.map((pump) => (
          <PumpChip key={pump.id} pump={pump} onClick={() => onOpenPump(pump)} />
        ))}
        <div className="flex flex-col items-center">
          <span className="h-3 w-[1.5px] opacity-0" />
          <button
            type="button"
            onClick={onAddPump}
            className="rounded-full border border-dashed border-gray-300 px-3 py-1.5 font-mono text-[11.5px] text-gray-400 transition hover:border-[#e2932b] hover:text-[#e2932b]"
          >
            + Pump
          </button>
        </div>
      </div>
    </section>
  )
}
